import { Outlet } from "react-router-dom";
import { subscribeWeb, unsubscribeWeb } from "finex-web";
import { useEffect, useState, useContext, useRef } from "react";
import {
  subscribe,
  unsubscribe,
  publish,
} from "../services/loadingServices/loading.service";
import Header from "../component/Header";
import Loading from "../component/Loading";
import { logout } from "../services/api/auth.service";
import ToastProvider from "../component/ToastProvider";
import SidebarCore from "../component/SidebarCore";
import { getListCompany, getListReports, getUserInfor } from "../services/api/account.service";
import ModulesContext, {
  MODULE_PERMISSION_CACHE_KEY,
} from "../context/ModulesContext";
import LanguageContext from "../context/LanguageContext";
import IconReport from "../assets/images/IconReport";
import PopupLogout from "../component/PopupLogout";
import SidebarCoreMobile from "../component/SidebarCoreMobile";
import RouteProgressBar from "../component/RouteProgressBar";
import BottomTabBar from "../component/BottomTabBar";
import SupportBanner from "../component/SupportBanner";
import {
  AccessContext,
  DEFAULT_ACCESS_CONTEXT,
  publishAccessContext,
  supportBannerFor,
} from "../services/accessContext";
import { resolveCurrentCompanyLogo } from "../services/currentCompany";
import { storedLocation } from "../services/currentLocation";
import { switchCompany } from "../services/companySwitch";
import { showApiError } from "../services/apiError";

const DashboardLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [message, setMessage] = useState({
    title: "Logged In on Another Device",
    desc: "Your session ended due to a login from another device. Please secure your account if it wasn't you.",
  });
  const [userInfor, setUserInfor] = useState<any>();
  const [listCompany, setListCompany] = useState<any>();
  const [listLanguage, setListLanguage] = useState<any>();
  const [accessContext, setAccessContext] = useState<AccessContext>(DEFAULT_ACCESS_CONTEXT);
  const [accessLoad, setAccessLoad] = useState<"loading" | "loaded" | "failed">("loading");
  const [companyLogo, setCompanyLogo] = useState<string>("");
  const { setModules, setIsLoading } = useContext(ModulesContext);
  const { language } = useContext(LanguageContext);

  const userId: string = localStorage.getItem("userId") || "";
  const companyId: string = localStorage.getItem("companyId") || "";
  const sessionId = localStorage.getItem("sessionId") || "";
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // The closed sidebar stays mounted off-screen; keep Tab and screen readers out of it.
  useEffect(() => {
    languageDropdownRef.current?.toggleAttribute("inert", !isSidebarOpen);
  }, [isSidebarOpen]);

  const getReports = async () => {
    const responseReport = await getListReports();
    return responseReport?.data?.rows
  }
  const toCamelCase = (str: string) => {
    return str
      .toLowerCase()
      .split(/[\s-_]+/)
      .map((word, index) => {
        if (index === 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join('');
  }

  const getAccountInfor = async (isLoading: boolean = false) => {
    try {
      setIsLoading(true); // Bắt đầu loading

      const response: any = await getUserInfor(
        "",
        localStorage.getItem("language") || "",
        userId,
        companyId
      );
      // Before any module page mounts, so pages read this session's context and never a default.
      const ctx = publishAccessContext(response.data.accessContext);
      setAccessContext(ctx);
      setAccessLoad("loaded");
      localStorage.setItem("companyName", ctx.currentCompanyName || response.data.company.companyName);

      const responseCompany: any = await getListCompany();
      setListCompany(responseCompany.data.data);
      resolveCurrentCompanyLogo(ctx, response.data, responseCompany.data.data).then(setCompanyLogo);
      setListLanguage(response?.data?.language);
      setUserInfor(response.data);
      let newModules = [...response.data.dataModules];
      window.modulesContext = {
        modulesContext: response.data.dataModules,
        setModules: () => { },
      };
      let menusItemNew: any = [];
      try {
        const valueReportsResponse = await getReports();
        if (valueReportsResponse?.length > 0) {
          menusItemNew = valueReportsResponse.map((v: any) => ({
            name: v.groupName,
            id: v.id,
            icon: IconReport,
            isActive: false,
            value: v.reports.map((item: any) => ({
              id: item.id,
              name: item.reportName,
              path: `/report/${item.id}`,
              value: true
            })),
          }));
          newModules = [...newModules, ...menusItemNew];
        }
      } catch (reportsError) {
        console.error("Reports failed to load, using core modules only:", reportsError);
      }
      if (newModules) {
        setModules(newModules);
        // /{lang}/403 renders outside this layout and so mounts with an empty
        // ModulesContext on a cold open; it reads this cache to send the user
        // to a module their role actually includes instead of /dashboard.
        try {
          localStorage.setItem(
            MODULE_PERMISSION_CACHE_KEY,
            JSON.stringify(newModules)
          );
        } catch {
          /* quota / private mode — the 403 page just falls back to /dashboard */
        }
      }
      publish("modulePermission", newModules);
      const temp: any = response.data?.locations.filter(
        (v: any) => v.isCurrentActive
      );
      // A support session keeps the branch it picked on this device (the API will not move the customer's).
      const keptSupportBranch = ctx.mode === "support" && !!storedLocation(response.data?.locations);
      if (temp.length > 0 && !keptSupportBranch) {
        localStorage.setItem("locationId", temp[0].id);
      }
    } catch (error) {
      console.error("Error loading account info:", error);
      setAccessLoad((state) => (state === "loaded" ? state : "failed"));
    } finally {
      setIsLoading(false); // Kết thúc loading
      if (isLoading) {
        publish("loading", false);
      }
    }
  };

  const handleClosePopup = async () => {
    const res = await logout({ sessionId });
    if (res.data.success) {
      const lang = localStorage.getItem("language");
      localStorage.clear();
      if (lang) {
        localStorage.setItem("language", lang);
      }
      window.location.href = "/";
    }
  };

  // A failed load leaves no context, so the token says whether this is support (without a way back).
  const supportBanner = supportBannerFor(accessLoad === "failed" ? undefined : accessContext);

  const handleBackToHome = async () => {
    if (!supportBanner?.homeCompanyId) return;
    publish("loading", true);
    try {
      // An editor with unsaved changes can hold the switch; its Discard runs this again.
      const left = await switchCompany(supportBanner.homeCompanyId, { resume: () => handleBackToHome() });
      if (!left) publish("loading", false);
    } catch (error) {
      showApiError(error, { language });
      publish("loading", false);
    }
  };

  const handleOpenPopup = () => {
    setShowPopup(true);
  };

  useEffect(() => {
    getAccountInfor(false);
  }, []);

  useEffect(() => {
    subscribe("reloadHeader", () => getAccountInfor(false));

    const handleTokenFailed = async () => {
      await getAccountInfor(false);
    };
    document.addEventListener("tokenFailed", handleTokenFailed);

    return () => {
      unsubscribe("reloadHeader", () => getAccountInfor(false));
      document.removeEventListener("tokenFailed", handleTokenFailed);
    };
  }, []);

  const handleLogout = async () => {
    const lang = localStorage.getItem("language");
    try {
      const response = await logout({
        sessionId: localStorage.getItem("sessionId") || "",
      });
      if (response.data.success) {
        localStorage.clear();
        if (lang) {
          localStorage.setItem("language", lang);
        }
        window.location.href = "/";
      }
    } catch (error: any) {
      console.error("Logout failed:", error);
      localStorage.clear();
      if (lang) {
        localStorage.setItem("language", lang);
      }
      window.location.href = "/";
    }
  };

  useEffect(() => {
    subscribeWeb("loadingWeb", (data: any) => {
      setLoading(data.detail);
    });
    subscribe("loading", (data: any) => {
      setLoading(data.detail);
    });
    subscribe("refreshTokenFailed", (data: any) => {
      setMessage({ title: data.detail.title, desc: data.detail.desc });
      setShowPopup(true);
    });
    subscribe("showSidebar", (data: any) => {
      setIsSidebarOpen(data.detail.value)
    });

    return () => {
      unsubscribeWeb("loadingWeb", () => { });
      unsubscribe("loading", () => { });
      unsubscribe("refreshTokenFailed", () => { });
      unsubscribe("showSidebar", () => { });
    };
  }, []);

  return (
    <div className="h-screen relative overflow-hidden">
      <RouteProgressBar />
      <PopupLogout
        title={language?.[message?.title] ?? message?.title}
        desc={message?.desc ? (language?.[message.desc] ?? message.desc) : undefined}
        confirmLabel={language?.["Confirm"] ?? "Confirm"}
        onClose={() => { handleLogout() }}
        show={showPopup}
        onOpen={() => { setShowPopup(false) }}
      />
      <Header
        userInfor={userInfor}
        listCompany={listCompany}
        listLanguage={listLanguage}
        getAccountInfor={getAccountInfor}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={() => setIsSidebarOpen((open) => !open)}
      />
      {supportBanner && (
        <SupportBanner
          companyName={supportBanner.companyName}
          language={language}
          canGoBack={!!supportBanner.homeCompanyId}
          onBack={handleBackToHome}
        />
      )}
      {/* On small screens .finex-shell-body (index.css) subtracts the fixed
          BottomTabBar's height (--finex-tabbar-h + safe area) so a page's last
          list row / pagination is never behind it. */}
      <div className="finex-shell-body w-full flex">
        <div className="lg:flex hidden h-full">
          <SidebarCore
            userInfor={userInfor}
            listCompany={listCompany}
            getAccountInfor={getAccountInfor}
            accessContext={accessContext}
            companyLogo={companyLogo}
          />
        </div>
        <div ref={languageDropdownRef}
          className={`fixed top-0 left-0 h-full bg-white shadow-lg z-40 transition-[transform,visibility] duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'
            }`}
        >
          <SidebarCoreMobile
            userInfor={userInfor}
            listCompany={listCompany}
            getAccountInfor={getAccountInfor}
            accessContext={accessContext}
            companyLogo={companyLogo}
          />
        </div>
        <div
          className={`max-lg:p-4 lg:p-6 w-full lg:w-[calc(100%-256px)] h-full bg-white ${loading ? "relative overflow-hidden" : ""
            }`}
        >
          {loading && <Loading />}
          <ToastProvider />
          <Outlet />
        </div>
      </div>
      <BottomTabBar />
    </div>
  );
};

export default DashboardLayout;
